import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Code2, Link as LinkIcon, ShieldAlert, UserPlus } from 'lucide-react';
import { paylinksApi } from '../../api/endpoints/paylinks';
import { productsApi } from '../../api/endpoints/products';
import { currencyApi } from '../../api/endpoints/currency';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CurrencyDisplay } from '../ui/CurrencyDisplay';
import { PermissionGate } from '../ui/PermissionGate';
import { useToast } from '../ui/Alert';
import { useStore } from '../../contexts/StoreContext';
import { getCdnUrl, getStoreUrl } from '../../lib/formatters';
import { ApiExampleModal } from './ApiExampleModal';
import { QuickAddEmployeeModal } from './QuickAddEmployeeModal';

interface QuickPaylinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  /** Pre-select a single product on open (used when launching from a product row). */
  preselectedProductId?: string;
  /** Fired after a successful create+copy. Use to invalidate paylink list queries, etc. */
  onCreated?: () => void;
}

/** Shared "create paylink" modal used from /products and /paylinks.
 *  Toggles between a fixed-amount paylink and a product-bundle paylink, then
 *  creates and copies the customer-facing link in `<store>.00stores.com`
 *  format (or the store's custom domain). */
export function QuickPaylinkModal({
  isOpen,
  onClose,
  storeId,
  preselectedProductId,
  onCreated,
}: QuickPaylinkModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentStore } = useStore();

  // A testing (unverified) store has no provisioned payment gateways, so the
  // backend refuses to create paylinks for it — same rule checkout enforces.
  // Block it in the UI too so the merchant gets an actionable message instead
  // of a link that dead-ends on the customer's side.
  const storeInTesting = !!currentStore?.testing;

  const [mode, setMode] = useState<'amount' | 'products'>(
    preselectedProductId ? 'products' : 'amount',
  );
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    preselectedProductId ? new Set([preselectedProductId]) : new Set(),
  );

  // Store currency → SAR auto-conversion. Payments always settle in SAR, so
  // when a merchant's store currency is something else (set in Settings) we
  // let them enter the amount in their own currency and convert it to SAR
  // before creating the paylink — same behaviour as the legacy EJS dashboard.
  const storeCurrency = (currentStore?.currency || 'SAR').toUpperCase();
  const needsConversion = storeCurrency !== 'SAR';
  // null = not yet converted; number = SAR value to actually charge.
  const [convertedSar, setConvertedSar] = useState<number | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertFailed, setConvertFailed] = useState(false);

  // One-time is the default. Merchants who want a reusable link tick the
  // "reusable" box. One-time links are paid once, then the backend stamps
  // `consumedAt` and blocks further checkouts on the same link.
  // Whatever the merchant last ticked persists across opens (per-browser),
  // so a merchant who creates many reusable links doesn't have to flip the
  // box every time.
  const REUSABLE_PREF_KEY = 'paylink.defaultReusable';
  const [reusable, setReusable] = useState(false);
  const linkMode: 'one-time' | 'recurring' = reusable ? 'recurring' : 'one-time';
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [showApiExample, setShowApiExample] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);

  // Reset whenever the modal opens (so reopening doesn't carry stale input).
  useEffect(() => {
    if (!isOpen) return;
    setMode(preselectedProductId ? 'products' : 'amount');
    setTitle('');
    setAmount('');
    setSelected(preselectedProductId ? new Set([preselectedProductId]) : new Set());
    let savedReusable = false;
    try { savedReusable = localStorage.getItem(REUSABLE_PREF_KEY) === '1'; } catch {}
    setReusable(savedReusable);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
  }, [isOpen, preselectedProductId]);

  const updateReusable = (next: boolean) => {
    setReusable(next);
    try { localStorage.setItem(REUSABLE_PREF_KEY, next ? '1' : '0'); } catch {}
  };

  // Live-convert the entered amount to SAR (debounced) whenever the merchant
  // types, but only in fixed-amount mode for a non-SAR store. Product paylinks
  // carry their own prices and are converted at checkout, so they're skipped.
  useEffect(() => {
    if (!needsConversion || mode !== 'amount') {
      setConvertedSar(null);
      setConverting(false);
      setConvertFailed(false);
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setConvertedSar(null);
      setConverting(false);
      setConvertFailed(false);
      return;
    }
    let cancelled = false;
    setConverting(true);
    setConvertFailed(false);
    const handle = setTimeout(() => {
      currencyApi
        .convert(amt, storeCurrency, 'SAR')
        .then((data) => {
          if (cancelled) return;
          const sar = Number(data.amount);
          if (Number.isFinite(sar) && sar > 0) {
            setConvertedSar(sar);
            setConvertFailed(false);
          } else {
            setConvertedSar(null);
            setConvertFailed(true);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setConvertedSar(null);
          setConvertFailed(true);
        })
        .finally(() => {
          if (!cancelled) setConverting(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [amount, mode, needsConversion, storeCurrency]);

  const { data: productsData } = useQuery({
    queryKey: ['products-for-paylink', storeId],
    queryFn: () => productsApi.list(storeId, { limit: 100 }),
    enabled: isOpen && !!storeId,
  });
  const products = productsData?.products ?? [];

  const createMutation = useMutation({
    mutationFn: (input: { title?: string; amount?: number; products?: Record<string, { defaultQuantity: number }> }) => {
      const prefilledCustomer =
        linkMode === 'one-time' && (customerName || customerEmail || customerPhone)
          ? {
              ...(customerName ? { name: customerName.trim() } : {}),
              ...(customerEmail ? { email: customerEmail.trim() } : {}),
              ...(customerPhone ? { phone: customerPhone.trim() } : {}),
            }
          : undefined;
      return paylinksApi.create(storeId, {
        title: input.title || t('products.quickPaylink', 'Quick Paylink'),
        ...(input.amount != null ? { amount: input.amount } : {}),
        products: input.products ?? {},
        mode: linkMode,
        ...(prefilledCustomer ? { prefilledCustomer } : {}),
      });
    },
    onSuccess: (data) => {
      const id = data.link || data.paylinkId;
      // One-time paylinks live at the Zero platform domain (no store branding
      // leaked); recurring stay on the merchant's storefront URL.
      const url = linkMode === 'one-time'
        ? `https://00pays.com/paylink/${id}`
        : `${getStoreUrl(currentStore, storeId)}/paylink/${id}`;
      navigator.clipboard.writeText(url).catch(() => {});
      toast('success', t('paylinks.linkCopied'));
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      onCreated?.();
      onClose();
    },
    onError: (err: any) => {
      // Surface the backend's reason (store in testing, payments disabled)
      // rather than a generic failure — it's the only actionable part.
      const reason = err?.details?.messageAr || err?.message;
      toast('error', reason || t('common.error'));
    },
  });

  const handleCreate = () => {
    if (storeInTesting) return;
    if (mode === 'amount') {
      const amt = Number(amount);
      if (!amt || amt <= 0) return;
      // Non-SAR store: charge the converted SAR value, not the raw input.
      // Guard against submitting before the conversion has resolved.
      if (needsConversion) {
        if (converting) return;
        if (convertFailed || convertedSar == null) {
          toast('error', t('paylinks.conversionUnavailable', 'Currency conversion unavailable, try again'));
          return;
        }
        createMutation.mutate({ title: title || undefined, amount: convertedSar });
        return;
      }
      createMutation.mutate({ title: title || undefined, amount: amt });
    } else {
      if (selected.size === 0) return;
      const productsMap: Record<string, { defaultQuantity: number }> = {};
      for (const id of selected) productsMap[id] = { defaultQuantity: 1 };
      createMutation.mutate({ title: title || undefined, products: productsMap });
    }
  };

  const toggleProduct = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const disabled =
    storeInTesting ||
    (mode === 'amount'
      ? !amount || Number(amount) <= 0 ||
        (needsConversion && (converting || convertFailed || convertedSar == null))
      : selected.size === 0);

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('products.quickPaylink', 'Quick Paylink')}
      size="lg"
      footer={
        <>
          <div className="me-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowApiExample(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-bg-tertiary px-3 py-1.5 text-xs font-medium text-text-secondary hover:border-accent/50 hover:text-accent transition-colors"
              title={t('paylinks.apiQuickReference', 'API Quick Reference')}
            >
              <Code2 className="h-3.5 w-3.5" />
              API
            </button>
            <PermissionGate requires="users.edit">
              <button
                type="button"
                onClick={() => setShowAddEmployee(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-bg-tertiary px-3 py-1.5 text-xs font-medium text-text-secondary hover:border-accent/50 hover:text-accent transition-colors"
                title={t('paylinks.addEmployeeForPaylinks', 'Invite a paylink creator')}
              >
                <UserPlus className="h-3.5 w-3.5" />
                {t('paylinks.addEmployee', 'Add employee')}
              </button>
            </PermissionGate>
          </div>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            isLoading={createMutation.isPending}
            onClick={handleCreate}
            disabled={disabled}
          >
            <LinkIcon className="h-4 w-4" />
            {t('products.createAndCopyLink', 'Create & Copy Link')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Store can't take payments yet: creating a link here would produce a
            URL that fails at the customer's card step, so block it up front. */}
        {storeInTesting && (
          <div className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t('paylinks.testingBlockedTitle', 'Your store cannot accept payments yet')}
              </p>
              <p className="mt-0.5 text-[11px] text-text-muted">
                {t(
                  'paylinks.testingBlockedBody',
                  'Payment links stay disabled while the store is in testing mode. Verify your identity to activate your store.',
                )}
              </p>
              <button
                type="button"
                onClick={() => { onClose(); navigate(`/${storeId}/verification`); }}
                className="mt-2 text-xs font-medium text-accent hover:underline"
              >
                {t('paylinks.testingBlockedCta', 'Verify your store')}
              </button>
            </div>
          </div>
        )}

        {/* One-time by default. Tick to make it reusable. */}
        <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-border-default bg-bg-secondary px-3 py-2.5 hover:border-accent/30 transition-colors">
          <input
            type="checkbox"
            checked={reusable}
            onChange={(e) => updateReusable(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-accent"
          />
          <span className="flex-1">
            <span className="block text-sm font-medium">
              {t('paylinks.reusableLink', 'Reusable link')}
            </span>
            <span className="block mt-0.5 text-[11px] text-text-muted">
              {t('paylinks.reusableLinkHint', 'Leave unchecked for a one-time bill to a known customer. Check to make a reusable link multiple customers can pay.')}
            </span>
          </span>
        </label>

        {/* Mode toggle */}
        <div className="flex gap-1 rounded-lg bg-bg-tertiary p-1">
          <button
            type="button"
            onClick={() => setMode('amount')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'amount'
                ? 'bg-accent text-white shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {t('products.fixedAmount', 'Fixed Amount')}
          </button>
          <button
            type="button"
            onClick={() => setMode('products')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'products'
                ? 'bg-accent text-white shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {t('products.selectProducts', 'Select Products')}
          </button>
        </div>

        <Input
          label={t('paylinks.title', 'Payment Link Title')}
          placeholder={t('products.quickPaylinkTitlePlaceholder', 'e.g. Custom order')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        {mode === 'amount' ? (
          <div>
            <Input
              label={t('common.amount', 'Amount')}
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {currentStore?.currency && (
              <p className="mt-1 text-xs text-text-muted">
                {t('products.currencyNote', 'Currency')}: {storeCurrency}
              </p>
            )}
            {/* Non-SAR store: live SAR preview. Payments settle in SAR, so the
                merchant sees exactly what the customer will be charged. */}
            {needsConversion && Number(amount) > 0 && (
              <p className="mt-1 text-xs">
                {converting ? (
                  <span className="text-text-muted">{t('paylinks.converting', 'Converting…')}</span>
                ) : convertFailed || convertedSar == null ? (
                  <span className="text-danger">{t('paylinks.conversionFailed', 'Conversion unavailable')}</span>
                ) : (
                  <span className="text-accent">
                    ≈ {convertedSar.toFixed(2)} SAR
                    <span className="text-text-muted">
                      {' '}{t('paylinks.chargedInSar', '(charged in SAR)')}
                    </span>
                  </span>
                )}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('products.selectProducts', 'Select Products')} ({selected.size})
            </label>
            <div className="max-h-60 overflow-y-auto space-y-1 rounded-lg border border-border-default p-2">
              {products.length === 0 ? (
                <p className="py-4 text-center text-sm text-text-muted">{t('common.noResults')}</p>
              ) : (
                products.map((p) => {
                  const isSelected = selected.has(p._id);
                  return (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => toggleProduct(p._id)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm transition-colors ${
                        isSelected ? 'bg-accent/10 text-accent border border-accent/30' : 'hover:bg-bg-hover border border-transparent'
                      }`}
                    >
                      <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? 'border-accent bg-accent text-white' : 'border-border-default'}`}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      {p.images?.[0] ? (
                        <img src={getCdnUrl(p.images[0], currentStore?._id)} alt="" className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-bg-tertiary" />
                      )}
                      <span className="flex-1 truncate">{p.title}</span>
                      <CurrencyDisplay amount={p.price} currency={currentStore?.currency} className="shrink-0" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* One-time only: optional customer info that gets snapshotted on the
            order so the merchant doesn't have to ask the buyer for their
            details at checkout. Skipped entirely for recurring links. */}
        {linkMode === 'one-time' && (
          <div className="rounded-lg border border-border-default bg-bg-secondary p-3 space-y-3">
            <div>
              <p className="text-sm font-medium">
                {t('paylinks.prefilledCustomerTitle', 'Customer info (optional)')}
              </p>
              <p className="mt-0.5 text-[11px] text-text-muted">
                {t('paylinks.prefilledCustomerHint', 'If you fill these, the buyer won\'t be asked again at checkout. Saved on the order.')}
              </p>
            </div>
            <Input
              label={t('common.name', 'Name')}
              placeholder={t('paylinks.customerNamePlaceholder', 'Customer name')}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('common.email', 'Email')}
                type="email"
                placeholder="customer@example.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
              <Input
                label={t('common.phone', 'Phone')}
                type="tel"
                placeholder="05XXXXXXXX"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
    <ApiExampleModal
      isOpen={showApiExample}
      onClose={() => setShowApiExample(false)}
      storeId={storeId}
      payload={{
        title: title || undefined,
        amount: needsConversion
          ? (convertedSar ?? undefined)
          : amount ? Number(amount) : undefined,
        customer:
          customerName || customerEmail || customerPhone
            ? {
                name: customerName || undefined,
                email: customerEmail || undefined,
                phone: customerPhone || undefined,
              }
            : undefined,
      }}
    />
    <QuickAddEmployeeModal
      isOpen={showAddEmployee}
      onClose={() => setShowAddEmployee(false)}
      storeId={storeId}
    />
    </>
  );
}
